export type JsaMapStatus = 'draft' | 'active' | 'completed' | 'archived';
export interface OperatorJsaSnapshot {
  formTemplateId?: string; formTitle?: string; formCategoryId?: string;
  id: string; status: JsaMapStatus; jsaNumber: number | null;
  wellId: string | null; wellName: string | null; apiNumber: string | null; operator: string | null;
  latitude: number | null; longitude: number | null; jobType: string | null;
  musterPointLat: number | null; musterPointLng: number | null; musterHighlightPath: string | null;
  musterPointBLat: number | null; musterPointBLng: number | null; musterHighlightPathB: string | null;
  windDirection: string | null; endedAt: string | null;
}
export interface OperatorJsaEvent { seq: number; at: string; id: string; snapshot: OperatorJsaSnapshot | null }
export interface OperatorJsaMapItem extends OperatorJsaSnapshot { ghost: boolean }
export const JSA_GHOST_MS = 12 * 60 * 60 * 1000;
export function jsaTime(value: string | null | undefined): number {
  if (!value) return NaN;
  return Date.parse(/(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : value.replace(' ', 'T') + 'Z');
}
export function operatorJsasAt(events: OperatorJsaEvent[], at: number, ghosts = true): OperatorJsaMapItem[] {
  const latest = new Map<string, OperatorJsaEvent>();
  for (const event of events) {
    if (jsaTime(event.at) <= at && event.seq > (latest.get(event.id)?.seq ?? -1)) latest.set(event.id, event);
  }
  return [...latest.values()].flatMap(({snapshot: s}) => {
    if (!s || !Number.isFinite(s.latitude) || !Number.isFinite(s.longitude) || Math.abs(s.latitude!)>90 || Math.abs(s.longitude!)>180) return [];
    const ghost = s.status === 'completed' || s.status === 'archived';
    const ended = jsaTime(s.endedAt);
    if (s.status !== 'active' && !(ghosts && ghost && ended <= at && at - ended < JSA_GHOST_MS)) return [];
    return [{ ...s, ghost }];
  });
}
export function jsaMapPath(raw: string | null): Array<{ lat: number; lng: number }> {
  try {
    const path = JSON.parse(raw || '[]');
    return Array.isArray(path) ? path.filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lng) && Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180).slice(0, 2000) : [];
  } catch { return []; }
}
// Explicit regulatory aliases only. Display/search's first-word matching is unsafe for authorization.
export function normalizedJsaOperator(value: string): string { return value.toLowerCase().replace(/[^a-z0-9]/g, ''); }
export function jsaOperatorAliases(name: string): string[] {
  const groups = [
    ['Mewbourne Oil Company','Mewbourne Oil Co'], ['EOG Resources','EOG Resources Inc'],
    ['Occidental Petroleum','OXY USA Inc','OXY USA WTP LP','Occidental Permian Ltd'],
    ['Chevron','Chevron USA Inc'], ['ExxonMobil','Exxon Mobil Corporation'], ['XTO Energy','XTO Energy Inc'],
    ['ConocoPhillips','ConocoPhillips Company'], ['Devon Energy','Devon Energy Production Company LP'],
    ['Diamondback Energy','Diamondback E&P LLC'], ['APA Corporation','Apache','Apache Corporation'],
    ['Coterra Energy','Coterra Energy Inc'], ['Matador Resources','Matador Production Company'],
    ['Permian Resources','Permian Resources Operating LLC'], ['Civitas Resources','Civitas Permian Operating LLC'],
    ['Ovintiv','Ovintiv USA Inc'], ['Hilcorp','Hilcorp Energy Company'], ['SM Energy','SM Energy Company'],
    ['Vital Energy','Vital Energy Inc'], ['BPX Energy','BPX Operating Company'], ['Shell','Shell Western E&P Inc'],
  ];
  const key = normalizedJsaOperator(name);
  const group = groups.find(g => g.some(n => normalizedJsaOperator(n) === key));
  return [...new Set((group || [name]).map(normalizedJsaOperator).filter(Boolean))];
}
